import {NavLink, Outlet} from "react-router-dom";
import React, {ReactNode, useMemo} from "react";
import { useTranslation } from "react-i18next";
import {
    CalendarDaysIcon,
    Cog6ToothIcon,
    HomeIcon,
    GiftIcon,
    BuildingOffice2Icon,
} from "@heroicons/react/24/outline";
import {
    CalendarDaysIcon as CalendarDaysIconSolid,
    Cog6ToothIcon as Cog6ToothIconSolid,
    HomeIcon as HomeIconSolid,
    GiftIcon as GiftIconSolid,
    BuildingOffice2Icon as BuildingOffice2IconSolid,
} from "@heroicons/react/24/solid";
import {CustomerSpaceBrandingProvider} from "../../pages/customer-space/CustomerSpaceBrandingContext";
import {useStateContext} from "../../contexts/ContextProvider";
import {ThemeBootstrap} from "../../pages/customer-space/hooks/useTheme";
import {Branding, useLoyaltyBranding} from "../../pages/settings/brandings/useBranding";
import {LoyaltyCartProvider} from "../../pages/customer-space/catalogs/LoyaltyCartContext";
import { CustomerLoyaltyProvider } from "../../pages/customer-space/CustomerLoyaltyContext";
import {useFacilityLoyaltyInfos} from "../../pages/loyalty/api/useLoyaltyProgram";
import {useLoyaltyLevelsPublic} from "../../pages/loyalty/api/useLoyaltyLevel";
import {useLoyaltyPreview} from "../../pages/customer-space/preview/LoyaltyPreviewContext";
import CSNotificationBanner from "./CSNotificationBanner";
import PostHogPageViewTracker from "../analytics/PostHogPageViewTracker";

const BASE_NAV_DEFS = [
    {to: "/customer-space/dashboard", labelKey: "nav.home", icon: HomeIcon, iconActive: HomeIconSolid},
    {to: "/customer-space/reservations", labelKey: "nav.stays", icon: CalendarDaysIcon, iconActive: CalendarDaysIconSolid},
    {to: "/customer-space/avantages", labelKey: "nav.benefits", icon: GiftIcon, iconActive: GiftIconSolid},
];

const HOTELS_NAV_DEF = {to: "/customer-space/hotels", labelKey: "nav.our_hotels", icon: BuildingOffice2Icon, iconActive: BuildingOffice2IconSolid};

const SETTINGS_NAV_DEF = {to: "/customer-space/settings", labelKey: "nav.more", icon: Cog6ToothIcon, iconActive: Cog6ToothIconSolid};

type Label = string;

type LayoutProps = {
    children?: ReactNode;
    activePage?: Label;
    /** branding live pour l’aperçu */
    brandingOverride?: Partial<Branding>;
};

export default function CustomerSpaceLayout({
                                                children = null,
                                                activePage,
                                                brandingOverride,
                                            }: LayoutProps) {
    const { t } = useTranslation('customer-space');
    const isPreview = !!children;
    const {user} = useStateContext();
    const isGroup = Boolean(user?.facility?.is_group);
    const navItems = useMemo(() => {
        const base = BASE_NAV_DEFS.map(d => ({ ...d, label: t(d.labelKey) }));
        const hotels = isGroup ? [{ ...HOTELS_NAV_DEF, label: t(HOTELS_NAV_DEF.labelKey) }] : [];
        const settings = { ...SETTINGS_NAV_DEF, label: t(SETTINGS_NAV_DEF.labelKey) };
        return [...base, ...hotels, settings];
    }, [isGroup, t]);
    const resolvedActivePage = activePage ?? t('nav.benefits');
    const storageKey = `loyaltyCart:${user?.facility_id ?? "x"}:${user?.id ?? "anon"}`;
    const {data:activeBranding} = useLoyaltyBranding(user.loyalty_program_id ?? undefined);

    const desktopItemClass = (active: boolean) =>
        [
            "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition",
            "hover:bg-white/10",
            active ? "bg-cs-text/15 text-cs-text" : "text-cs-text/80",
            isPreview ? "cursor-default" : "",
        ].join(" ");

    const mobileItemClass = (active: boolean) =>
        [
            "mx-1 flex h-12 flex-col items-center justify-center rounded-md px-1 leading-none transition",
            active ? "bg-cs-text/10 text-cs-text" : "text-cs-text/80 hover:text-cs-text hover:bg-cs-bg/10",
            isPreview ? "cursor-default" : "",
        ].join(" ");

    // Loyalty data
    const preview = useLoyaltyPreview();
    const { data: facilityLoyaltyInfos } = useFacilityLoyaltyInfos(user.facility_id);
    // Niveaux : preview > API
    const { data: levelsResp } = useLoyaltyLevelsPublic(user.loyalty_program_id ?? undefined);
    const apiLevels = (Array.isArray(levelsResp) ? levelsResp : levelsResp?.data) ?? [];
    const levels = (preview.levels ?? apiLevels).slice().sort((a,b) => (a.quantity ?? 0) - (b.quantity ?? 0));

    const loyaltyInitial = useMemo(() => ({
        earningName: preview.info?.earning_name ?? facilityLoyaltyInfos?.earning_name ?? "Points",
        loyalty_points: user?.loyalty_points ?? 1147,
        loyalty_level_points: user?.loyalty_level_points ?? 212,
        levels: levels ?? [],
    }), [preview.info?.earning_name, facilityLoyaltyInfos?.earning_name, user?.loyalty_points, user?.loyalty_level_points, levels]);


    return (
        <LoyaltyCartProvider storageKey={storageKey}>
            <CustomerLoyaltyProvider initial={loyaltyInitial}>

                <PostHogPageViewTracker />
                <CSNotificationBanner/>
                <ThemeBootstrap/>
                <CustomerSpaceBrandingProvider facilityId={user.facility_id}
                                               brandingOverride={brandingOverride || activeBranding}>
                    <div className="flex flex-col min-h-screen bg-cs-bg">
                        {/* NAV DESKTOP */}
                        <aside
                            className="fixed inset-y-0 left-0 z-40 hidden md:flex w-64 flex-col bg-cs-menu border-r border-white/10">
                            <div className="flex flex-col gap-3 px-4 items-start py-4">
                                {activeBranding?.logo_path ?
                                <img src={activeBranding.logo_path} alt="Logo" className="h-20 w-auto mx-auto"/>
                                : <span className="text-2xl font-bold text-white">Mon espace</span>
                                }
                            </div>

                            <nav className="px-3 pb-4 space-y-1 h-full">
                                <div className="flex flex-col justify-between h-full">
                                    <div className="flex flex-col gap-2">
                                        {navItems.map(({to, label, icon: Icon, iconActive: IconActive}) => (
                                            <NavLink
                                                key={to}
                                                to={to}
                                                // Empêche la navigation en mode aperçu
                                                onClick={isPreview ? (e) => e.preventDefault() : undefined}
                                                aria-disabled={isPreview}
                                                tabIndex={isPreview ? -1 : 0}
                                                className={({isActive}) =>
                                                    desktopItemClass(isPreview ? label === resolvedActivePage : isActive)
                                                }
                                            >
                                                {({isActive}) => {
                                                    const active = isPreview ? label === resolvedActivePage : isActive;
                                                    const CurrentIcon = active ? IconActive : Icon;
                                                    return (
                                                        <>
                                                            <CurrentIcon className="h-6 w-6 shrink-0"/>
                                                            <span>{label}</span>
                                                        </>
                                                    );
                                                }}
                                            </NavLink>
                                        ))}
                                    </div>
                                </div>
                            </nav>
                        </aside>

                        {/* CONTENU */}
                        <main
                            className="ml-0 md:ml-64 flex-1 flex flex-col min-h-0 md:pb-16 pb-[calc(65px+env(safe-area-inset-bottom))]">
                            {children ?? <Outlet/>}
                        </main>

                        {/* NAV MOBILE */}
                        <nav
                            className="fixed bottom-0 left-0 right-0 z-40 flex h-14 items-stretch justify-between bg-cs-menu md:hidden"
                            style={{paddingBottom: "env(safe-area-inset-bottom)"}}
                        >
                            <ul className="flex w-full items-center justify-around px-2">
                                {navItems.map(({to, label, icon: Icon, iconActive: IconActive}) => (
                                    <li key={to} className="flex-1">
                                        <NavLink
                                            to={to}
                                            onClick={isPreview ? (e) => e.preventDefault() : undefined}
                                            aria-disabled={isPreview}
                                            tabIndex={isPreview ? -1 : 0}
                                            className={({isActive}) =>
                                                mobileItemClass(isPreview ? label === resolvedActivePage : isActive)
                                            }
                                        >
                                            {({isActive}) => {
                                                const active = isPreview ? label === resolvedActivePage : isActive;
                                                const CurrentIcon = active ? IconActive : Icon;
                                                return (
                                                    <>
                                                        <CurrentIcon className={`h-6 w-6 transition-transform ${active ? "scale-110" : ""}`}/>
                                                        <span className={`text-[11px] mt-0.5 ${active ? "font-semibold" : "font-medium"}`}>{label}</span>
                                                    </>
                                                );
                                            }}
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </CustomerSpaceBrandingProvider>
            </CustomerLoyaltyProvider>
        </LoyaltyCartProvider>
    );
}